import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";
import { HiChevronDown, HiChevronRight } from "react-icons/hi2";
import { User as UserIcon } from "@phosphor-icons/react";

function getChildDimensionName(item, dimensions) {
  if (!dimensions || dimensions.length === 0) return null;
  const currentIndex = dimensions.findIndex((d) => d.level === item.dimensionLevel);
  if (currentIndex >= 0 && currentIndex < dimensions.length - 1) {
    return dimensions[currentIndex + 1].name;
  }
  return null;
}

function DimensionTreeNode({ item, depth, showInactive, dimensions, isUnlinked, parentItemId, parentAssigneeEmail, isESGAdmin, onCheckUsage, onEditItem, onDeleteItem, onReactivateItem, onAddChild, onLinkItem, onMergeItem }) {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: false });
  const isArchived = item.state === "ARCHIVED";
  const hasChildren = item.children && item.children.length > 0;
  const childDimensionName = getChildDimensionName(item, dimensions);
  const isLevel1 = dimensions.length > 0 && item.dimensionLevel === dimensions[0].level;

  const visibleChildren = hasChildren
    ? item.children.filter((child) => showInactive || child.state !== "ARCHIVED")
    : [];

  if (!showInactive && isArchived) return null;

  return (
    <Box>
      <Flex
        align="center"
        py={1.5}
        px={2}
        pl={`${depth * 28 + 8}px`}
        _hover={{ bg: "gray.100" }}
        borderRadius="md"
        opacity={isArchived ? 0.5 : 1}
        gap={2}
      >
        {/* Expand/collapse toggle */}
        <IconButton
          icon={isOpen ? <HiChevronDown /> : <HiChevronRight />}
          size="xs"
          variant="ghost"
          aria-label="Toggle children"
          onClick={onToggle}
          visibility={visibleChildren.length > 0 ? "visible" : "hidden"}
          minW="20px"
        />

        {/* Item name — click to rename */}
        <Text
          fontSize="sm"
          fontWeight={depth === 0 ? "semibold" : "normal"}
          flex="1"
          cursor="pointer"
          _hover={{ textDecoration: "underline", color: "limeShade.600" }}
          onClick={() => onEditItem(item)}
        >
          {item.name}
        </Text>

        {/* Default assignee — inherited rows show an icon-only pill so we
            don't repeat the same email down the tree. */}
        {item.defaultAssigneeEmail && (
          item.defaultAssigneeEmail === parentAssigneeEmail ? (
            <Tooltip label={`${item.defaultAssigneeEmail} (same as parent)`} placement="top">
              <Tag
                size="sm"
                borderRadius="full"
                bg="lightLime"
                color="limeShade.700"
                border="1px solid"
                borderColor="lime"
                px={1.5}
              >
                <UserIcon size={11} weight="fill" />
              </Tag>
            </Tooltip>
          ) : (
            <Tag
              size="sm"
              borderRadius="full"
              bg="lightLime"
              color="limeShade.700"
              border="1px solid"
              borderColor="lime"
              px={2}
              maxW="240px"
            >
              <HStack spacing={1.5} minW={0}>
                <UserIcon size={11} weight="fill" />
                <TagLabel fontSize="11px" fontWeight="600" isTruncated>
                  {item.defaultAssigneeEmail}
                </TagLabel>
              </HStack>
            </Tag>
          )
        )}

        {/* Dimension level badge */}
        <Badge colorScheme="purple" fontSize="2xs" variant="subtle">
          {item.dimensionName}
        </Badge>

        {/* Shared indicator for multi-parent items */}
        {item.parentCount > 1 && (
          <Badge colorScheme="orange" fontSize="2xs" variant="outline">
            shared
          </Badge>
        )}

        {/* Status badge */}
        <Badge colorScheme={isArchived ? "gray" : "green"} fontSize="2xs">
          {item.state || "ACTIVE"}
        </Badge>

        {/* Actions */}
        <Flex gap={1} ml={2}>
          <Button size="xs" variant="ghost" fontSize="xs" onClick={() => onCheckUsage(item)}>
            Usage
          </Button>
          {isESGAdmin && !isArchived && (
            <Button size="xs" variant="ghost" fontSize="xs" colorScheme="purple" onClick={() => onMergeItem(item)}>
              Merge
            </Button>
          )}
          {isESGAdmin && childDimensionName && (
            <Button size="xs" variant="ghost" fontSize="xs" colorScheme="teal" onClick={() => onAddChild(item, childDimensionName)}>
              + {childDimensionName}
            </Button>
          )}
          {isESGAdmin && !isLevel1 && onLinkItem && (
            <Button size="xs" variant="ghost" fontSize="xs" colorScheme="blue" onClick={() => onLinkItem(item)}>
              {isUnlinked ? "Link" : "Link to Parent"}
            </Button>
          )}
          {isESGAdmin && (isArchived ? (
            <Button size="xs" variant="ghost" fontSize="xs" colorScheme="green" onClick={() => onReactivateItem(item)}>
              Reactivate
            </Button>
          ) : (
            <Button size="xs" variant="ghost" fontSize="xs" colorScheme="red" onClick={() => onDeleteItem(item)}>
              Delete
            </Button>
          ))}
        </Flex>
      </Flex>

      {/* Children */}
      {item.childrenOmitted ? (
        <Text
          fontSize="xs"
          color="gray.400"
          fontStyle="italic"
          pl={`${(depth + 1) * 28 + 28}px`}
          py={1}
        >
          Children shown under first parent occurrence
        </Text>
      ) : (
        <Collapse in={isOpen} animateOpacity>
          {visibleChildren.map((child) => (
            <DimensionTreeNode
              key={child.id}
              item={child}
              depth={depth + 1}
              showInactive={showInactive}
              dimensions={dimensions}
              isUnlinked={false}
              parentItemId={item.id}
              parentAssigneeEmail={item.defaultAssigneeEmail || null}
              isESGAdmin={isESGAdmin}
              onCheckUsage={onCheckUsage}
              onEditItem={onEditItem}
              onDeleteItem={onDeleteItem}
              onReactivateItem={onReactivateItem}
              onAddChild={onAddChild}
              onLinkItem={onLinkItem}
              onMergeItem={onMergeItem}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
}

export default React.memo(DimensionTreeNode);
